import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Modal,
  message,
} from "antd";
import axios from "axios";
import dayjs from "dayjs";

type ITRequestPayload = {
  system_name: string;
  issue_type: "Access Request" | "Problem";
  priority: "Low" | "Medium" | "High" | "Critical";
  assigned_to?: "IT Specialist" | "Operations" | "CT Support" | null;
  access_needed_by?: string | null;
  affected_user_name: string;
  affected_user_email: string;
  affected_user_phone?: string | null;
  comments?: string | null;
};

const { TextArea } = Input;

const HelpDeskRequestForm: React.FC = () => {
  const [form] = Form.useForm();

  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successId, setSuccessId] = useState<number | null>(null);

  const issueType = Form.useWatch("issue_type", form);

  const onSubmit = async () => {
    setSubmitting(true);

    try {
      const values = await form.validateFields();

      const payload: ITRequestPayload = {
        system_name: values.system_name?.trim(),
        issue_type: values.issue_type,
        priority: values.priority,
        assigned_to: values.assigned_to || null,
        access_needed_by:
          values.issue_type === "Access Request" && values.access_needed_by
            ? dayjs(values.access_needed_by).format("YYYY-MM-DD HH:mm:ss")
            : null,
        affected_user_name: values.affected_user_name?.trim(),
        affected_user_email: values.affected_user_email?.trim(),
        affected_user_phone: values.affected_user_phone?.trim()
          ? values.affected_user_phone.trim()
          : null,
        comments: values.comments?.trim() ? values.comments.trim() : null,
      };

      const res = await axios.post("/api/it-requests", payload);

      const requestId = res?.data?.it_request_id ?? null;
      setSuccessId(requestId);
      setSuccessOpen(true);

      form.resetFields();
      form.setFieldsValue({
        issue_type: "Problem",
        priority: "Medium",
      });
    } catch (err: any) {
      console.error(err);
      message.error(
        err?.response?.data?.error ||
          "Submit failed. Check required fields and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card style={{ maxWidth: 1000, margin: "0 auto" }}>
      <h2 style={{ marginBottom: 0 }}>Help Desk Request</h2>
      <p style={{ marginTop: 8, opacity: 0.8 }}>
        Submit an IT access request or report a problem.
      </p>

      <Form
        form={form}
        layout='vertical'
        initialValues={{
          issue_type: "Problem",
          priority: "Medium",
        }}
      >
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <Form.Item
            label='System Name'
            name='system_name'
            rules={[{ required: true, message: "System name is required." }]}
          >
            <Input placeholder='e.g., Bullhorn, Datacron, Email, VPN' />
          </Form.Item>

          <Form.Item
            label='Issue Type'
            name='issue_type'
            rules={[{ required: true, message: "Issue type is required." }]}
          >
            <Select
              options={[
                { value: "Access Request", label: "Access Request" },
                { value: "Problem", label: "Problem" },
              ]}
            />
          </Form.Item>

          <Form.Item
            label='Priority'
            name='priority'
            rules={[{ required: true, message: "Priority is required." }]}
          >
            <Select
              options={[
                { value: "Low", label: "Low" },
                { value: "Medium", label: "Medium" },
                { value: "High", label: "High" },
                { value: "Critical", label: "Critical" },
              ]}
            />
          </Form.Item>

          {/* <Form.Item label='Assigned To' name='assigned_to'>
            <Select
              allowClear
              placeholder='Select team'
              options={[
                { value: "IT Specialist", label: "IT Specialist" },
                { value: "Operations", label: "Operations" },
                { value: "CT Support", label: "CT Support" },
              ]}
            />
          </Form.Item> */}
        </div>

        {issueType === "Access Request" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}
          >
            <Form.Item label='Access Needed By' name='access_needed_by'>
              <DatePicker
                showTime
                style={{ width: "100%" }}
                format='YYYY-MM-DD HH:mm'
              />
            </Form.Item>
          </div>
        )}

        <h4 style={{ marginTop: 8 }}>Affected User</h4>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <Form.Item
            label='Affected User Name'
            name='affected_user_name'
            rules={[
              { required: true, message: "Affected user name is required." },
            ]}
          >
            <Input placeholder='e.g., Jane Doe' />
          </Form.Item>

          <Form.Item
            label='Affected User Email'
            name='affected_user_email'
            rules={[
              { required: true, message: "Affected user email is required." },
              { type: "email", message: "Enter a valid email address." },
            ]}
          >
            <Input placeholder='e.g., jane@company.com' />
          </Form.Item>

          <Form.Item
            label='Affected User Phone (optional)'
            name='affected_user_phone'
          >
            <Input placeholder='e.g., 555-555-5555' />
          </Form.Item>
        </div>

        <Form.Item label='Comments' name='comments'>
          <TextArea
            rows={5}
            placeholder='Describe the issue, requested access, impact, error messages, or any other details.'
          />
        </Form.Item>

        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
        >
          <Button type='primary' loading={submitting} onClick={onSubmit}>
            Submit Request
          </Button>
        </div>
      </Form>

      <Modal
        open={successOpen}
        onCancel={() => setSuccessOpen(false)}
        onOk={() => setSuccessOpen(false)}
        title='Request Submitted'
      >
        <p>
          Help desk request saved successfully
          {successId ? ` (ID: ${successId})` : ""}.
        </p>
      </Modal>
    </Card>
  );
};

export default HelpDeskRequestForm;
